const STORAGE_KEY = 'mm_device_fp'

/**
 * MurmurHash3-inspired 64-bit hash (produces a base-36 string).
 * Used to produce a compact, stable fingerprint from browser characteristics.
 */
function murmurhash(str: string): string {
  let h1 = 0xdeadbeef
  let h2 = 0x41c6ce57
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i)
    h1 = Math.imul(h1 ^ ch, 2654435761)
    h2 = Math.imul(h2 ^ ch, 1597334677)
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507)
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909)
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507)
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909)
  const combined = 4294967296 * (2097151 & h2) + (h1 >>> 0)
  return Math.abs(combined).toString(36)
}

/**
 * Renders an invisible canvas and returns the first 100 characters of its data URL.
 * Different GPU drivers and font rendering engines produce subtly different output,
 * making this one of the most stable cross-browser differentiators.
 */
function getCanvasFingerprint(): string {
  try {
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    if (!ctx) return ''
    ctx.textBaseline = 'top'
    ctx.font = '14px "Arial"'
    ctx.fillStyle = '#f60'
    ctx.fillRect(125, 1, 62, 20)
    ctx.fillStyle = '#069'
    ctx.fillText('MultiMagic', 2, 15)
    ctx.fillStyle = 'rgba(102, 204, 0, 0.7)'
    ctx.fillText('device', 4, 17)
    return canvas.toDataURL().slice(0, 100)
  } catch {
    return ''
  }
}

/**
 * Computes a fingerprint string from stable, non-privacy-sensitive browser characteristics.
 * This is intentionally NOT a secret — its purpose is to differentiate browsers/devices,
 * not to authenticate users. The server stores a SHA-256 hash of this value.
 */
function computeRaw(): string {
  const deviceMemory = (navigator as Navigator & { deviceMemory?: number }).deviceMemory ?? 0
  const components = [
    navigator.userAgent,
    navigator.language,
    Intl.DateTimeFormat().resolvedOptions().timeZone,
    `${screen.width}x${screen.height}x${screen.colorDepth}`,
    String(navigator.hardwareConcurrency ?? 0),
    String(deviceMemory),
    getCanvasFingerprint(),
  ]
  return components.join('||')
}

/**
 * Returns a stable device fingerprint for this browser.
 * The value is cached in localStorage so it remains the same across sessions and tabs.
 * The fingerprint is intentionally NOT cleared on logout — doing so would cause a
 * re-authentication cycle if a user clears and re-logs in the same browser.
 */
export function getDeviceFingerprint(): string {
  const cached = localStorage.getItem(STORAGE_KEY)
  if (cached) return cached
  const fp = murmurhash(computeRaw())
  localStorage.setItem(STORAGE_KEY, fp)
  return fp
}
